import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createLocalVideoTrack, LocalVideoTrack } from 'livekit-client';
import { useSnackbar } from '@web-chat-core/ui-kit';
import { useUserSettingsContext } from 'contexts';
import { getLogger, LOGGERS } from 'lib/logger';
import { virtualBackgroundService as service } from 'features/VirtualBackground/classes/VirtualBackgroundService';
import { virtualBackgroundStore } from 'features/VirtualBackground/state/store/virtualBackground';
import { selectLocalVideoTrack } from 'state/selectors/localTracks';
import { setLocalVideoTrack } from 'useCase/localTracks/useLocalTracks';
import { isMediaAccessDenied } from '../utils';

const logger = getLogger(LOGGERS.WaitingRoom);

export const useManageVideoDevices = () => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { videoEnabled, setVideoEnabled, videoDeviceId, setVideoDeviceId, permissions } = useUserSettingsContext();
    const localVideoTrack = selectLocalVideoTrack();
    // Ждём гидратации стора фонов — без неё не знаем какой процессор применять
    const isVirtualBackgroundStoreHydrated = virtualBackgroundStore.isHydrated;
    const abortControllerRef = useRef<AbortController | null>(null);
    // 🔑 Используем ref для доступа к актуальному треку в эффектах и cleanup
    const localVideoTrackRef = useRef<LocalVideoTrack | null>(null);
    localVideoTrackRef.current = localVideoTrack;

    const clearCurrentTrack = useCallback(async () => {
        const track = localVideoTrackRef.current;
        setLocalVideoTrack(null);
        localVideoTrackRef.current = null;
        // Сначала останавливаем трек (освобождаем камеру),
        // потом чистим менеджер — иначе Room может не захватить камеру на некоторых устройствах
        if (track) {
            track.stop();
        }
        await service.removeManager('prejoin');
    }, []);

    const toggleVideo = useCallback(() => {
        const next = !videoEnabled;
        if (next && permissions.video === false) {
            showSnackbar(
                t('videoconference_camera_access_description', {
                    app_name: process.env.REACT_APP_PRODUCT_NAME ?? 'Meet',
                }),
                {
                    testId: 'snackbar-camera-access-denied',
                },
            );
            return;
        }
        setVideoEnabled(next);
    }, [permissions.video, setVideoEnabled, showSnackbar, t, videoDeviceId, videoEnabled]);

    const selectVideoDevice = useCallback(
        ({ deviceId }: MediaDeviceInfo) => {
            setVideoDeviceId(deviceId);
        },
        [setVideoDeviceId],
    );

    useEffect(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (!videoEnabled) {
            clearCurrentTrack();
            return;
        }

        if (permissions.video === false) {
            setVideoEnabled(false);
            clearCurrentTrack();
            return;
        }

        if (!isVirtualBackgroundStoreHydrated) {
            return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const createTrack = async () => {
            try {
                const track = await createLocalVideoTrack({
                    deviceId: videoDeviceId,
                });

                if (controller.signal.aborted) {
                    track.stop();
                    return;
                }

                // Критично: процессор применяется ДО записи трека в стор,
                // иначе компонент отрендерит сырой кадр без фона
                try {
                    await service.applyToTrack('prejoin', track);
                } catch (error) {
                    logger.error('Failed to apply prejoin background processor:', error);
                }

                if (controller.signal.aborted) {
                    track.stop();
                    await service.removeManager('prejoin');
                    return;
                }

                // Атомарная замена: сначала записываем новый трек, потом останавливаем старый,
                // чтобы не было моргания пустым видео между треками
                const oldTrack = localVideoTrackRef.current;
                setLocalVideoTrack(track);
                localVideoTrackRef.current = track;

                if (oldTrack && oldTrack !== track) {
                    oldTrack.stop();
                }
            } catch (e) {
                if (isMediaAccessDenied(e)) {
                    setVideoEnabled(false);
                    return;
                }
                logger.error('Failed to create video track', e);
            }
        };

        createTrack();

        return () => {
            controller.abort();
        };
    }, [
        clearCurrentTrack,
        isVirtualBackgroundStoreHydrated,
        permissions.video,
        setVideoEnabled,
        videoDeviceId,
        videoEnabled,
    ]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            clearCurrentTrack();
        };
    }, [clearCurrentTrack]);

    return {
        selectVideoDevice,
        toggleVideo,
        videoTrack: localVideoTrack,
    };
};
